/** @fileoverview Works out the apps domain and builder URL from the page hostname. */

import {
  APP_NAME,
  APPS_BASE_DOMAIN,
  BUILDER_BASE_DOMAIN,
  PLATFORM_API_PREFIX,
  PLATFORM_API_SUFFIX,
} from '../constants';
import { config } from '../config';

const ENV_LABEL = '[a-z0-9-]+';
const BUILDER_HOST_PATTERN = new RegExp(
  `^${APP_NAME}(?:-(${ENV_LABEL}))?\\.${BUILDER_BASE_DOMAIN.replace('.', '\\.')}$`
);
const APP_ENV_PATTERN = new RegExp(
  `^(?:(${ENV_LABEL})\\.)?${APPS_BASE_DOMAIN.replace('.', '\\.')}$`
);

/** Returns the apps domain for a hostname, or '' when the hostname does not follow the pattern. */
export const getAppsDomain = (hostname: string) => {
  if (config.appsDomain) {
    return config.appsDomain;
  }
  const host = hostname.toLowerCase();
  if (import.meta.env.DEV && (host === 'localhost' || host.endsWith('.localhost'))) {
    return 'localhost';
  }
  const builderMatch = BUILDER_HOST_PATTERN.exec(host);
  if (builderMatch) {
    const [, env] = builderMatch;
    return env ? `${env}.${APPS_BASE_DOMAIN}` : APPS_BASE_DOMAIN;
  }
  if (!host.endsWith(`.${APPS_BASE_DOMAIN}`)) {
    return '';
  }
  const parentDomain = host.split('.').slice(1).join('.');
  return APP_ENV_PATTERN.test(parentDomain) ? parentDomain : '';
};

/** Returns the builder URL for a hostname, or '' when the hostname is not an app address. */
export const getBuilderUrl = (hostname: string) => {
  if (config.builderUrl) {
    return config.builderUrl;
  }
  const host = hostname.toLowerCase();
  if (!host.endsWith(`.${APPS_BASE_DOMAIN}`)) {
    return '';
  }
  const appsDomain = host.split('.').slice(1).join('.');
  const envMatch = APP_ENV_PATTERN.exec(appsDomain);
  if (!envMatch) {
    return '';
  }
  const [, env] = envMatch;
  return `https://${APP_NAME}${env ? `-${env}` : ''}.${BUILDER_BASE_DOMAIN}`;
};

/** Returns the gateway URL for an app page hostname, or '' when the hostname is not an app address. */
export const getGatewayUrl = (hostname: string) => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:2000';
  }
  const host = hostname.toLowerCase();
  if (!host.endsWith(`.${APPS_BASE_DOMAIN}`)) {
    return '';
  }
  const envMatch = APP_ENV_PATTERN.exec(host.split('.').slice(1).join('.'));
  if (!envMatch) {
    return '';
  }
  const [, env] = envMatch;
  return `https://${PLATFORM_API_PREFIX}${
    env ? `-${env}` : ''
  }-${PLATFORM_API_SUFFIX}.${BUILDER_BASE_DOMAIN}`;
};

/** Reads the apps domain for the current page. */
export const getCurrentAppsDomain = () => getAppsDomain(window.location.hostname);

/** Reads the builder URL for the current page. */
export const getCurrentBuilderUrl = () => getBuilderUrl(window.location.hostname);

/** Reads the gateway URL for the current page. */
export const getCurrentGatewayUrl = () => getGatewayUrl(window.location.hostname);

/** Builds the hosted app address (or its preview address), or '' when the apps domain is unknown. */
export const getHostedAppUrl = ({ slug, preview }: { slug: string; preview?: boolean }) => {
  const appsDomain = getCurrentAppsDomain();
  if (!appsDomain || !slug) {
    return '';
  }
  const local = appsDomain === 'localhost' || appsDomain.endsWith('.localhost');
  const { port } = window.location;
  const host = `${slug}${preview ? '--preview' : ''}.${appsDomain}`;
  return `${local ? 'http' : 'https'}://${host}${local && port ? `:${port}` : ''}`;
};
